"use client";

import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import Map, { Marker, Source, Layer, NavigationControl } from "react-map-gl/maplibre";
import type { MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { MAPLIBRE_WORKER_URL } from "@/app/_module/utils/maplibreWorker";
import { Car, Footprints, Pencil } from "lucide-react";
import type { StopDetail } from "../types/types";

export interface HeatmapPoint {
  lat: number;
  lng: number;
  weight?: number;
}

interface RouteMapProps {
  stops: StopDetail[];
  routeGeometry?: string;
  activeStopIndex?: number;
  // Lead ID of the currently-active stop. Preferred over activeStopIndex
  // for lookups because the map filters out stops with invalid coords,
  // which shifts internal array indices vs the parent's stopDetails.
  // Side panel passes index from unfiltered list → mismatch. Looking
  // up by leadId is safe regardless of filtering.
  activeStopLeadId?: string | null;
  onStopClick?: (leadId: string) => void;
  // Fires when the user taps map background (not a stop marker). Lets the
  // parent dismiss overlays like the disposition dock when the agent taps
  // away from any pin.
  onMapBackgroundClick?: () => void;
  liveTracking?: boolean;
  // Fires on every GPS fix from the map's own watchPosition. Lets the parent
  // (route detail's 15s live-ping loop) reuse THIS watch instead of running a
  // second high-accuracy getCurrentPosition consumer — one GPS source total,
  // roughly half the battery drain while driving a route.
  onPositionUpdate?: (pos: {
    lat: number;
    lng: number;
    accuracy?: number | null;
    speed?: number | null;
    heading?: number | null;
  }) => void;
  radiusMiles?: number;
  showRadius?: boolean;
  allowPinDrop?: boolean;
  onPinDrop?: (lat: number, lng: number) => void;
  pinDropLocation?: { lat: number; lng: number } | null;
  heatmapData?: HeatmapPoint[];
  heatmapType?: "knocks" | "conversions" | "cold";
  showHeatmap?: boolean;
  // Drive/Walk toggle — when provided, renders a control next to the
  // zoom controls and lets the parent re-optimize on change.
  travelMode?: "driving" | "walking";
  onTravelModeChange?: (mode: "driving" | "walking") => void;
  travelModeDisabled?: boolean;
  // Pin-edit mode: when set to a leadId, that stop's marker is draggable.
  // Drop event fires `onPinDragEnd` with the new lat/lng so the parent can
  // persist it to the lead record.
  editingPinForLeadId?: string | null;
  onPinDragEnd?: (leadId: string, lat: number, lng: number) => void;
  className?: string;
}

// Generate a GeoJSON circle from a center point and radius
function createGeoJSONCircle(lng: number, lat: number, radiusMiles: number, points = 64): any {
  const km = radiusMiles * 1.60934;
  const coords = [];
  const distanceX = km / (111.32 * Math.cos((lat * Math.PI) / 180));
  const distanceY = km / 110.574;

  for (let i = 0; i < points; i++) {
    const theta = (i / points) * (2 * Math.PI);
    const x = distanceX * Math.cos(theta);
    const y = distanceY * Math.sin(theta);
    coords.push([lng + x, lat + y]);
  }
  coords.push(coords[0]); // close the ring

  return {
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [coords] },
  };
}

// Vector-tile styles served free by OpenFreeMap (https://openfreemap.org).
// No API key, no rate limit, hosted on Bunny CDN. Replaces Mapbox-hosted
// tiles so we don't need a Mapbox account or pay per-load. The "satellite"
// option uses Esri's free World Imagery raster layer wrapped in a minimal
// MapLibre style spec because OpenFreeMap is vector-only.
const MAP_STYLES = {
  light: "https://tiles.openfreemap.org/styles/positron",
  streets: "https://tiles.openfreemap.org/styles/liberty",
  satellite: {
    version: 8,
    sources: {
      "esri-imagery": {
        type: "raster",
        tiles: [
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        ],
        tileSize: 256,
        attribution:
          "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community",
      },
    },
    layers: [{ id: "esri-imagery", type: "raster", source: "esri-imagery" }],
  } as any,
};

export function RouteMap({
  stops: stopsRaw,
  routeGeometry,
  activeStopIndex,
  activeStopLeadId,
  onStopClick,
  onMapBackgroundClick,
  travelMode,
  onTravelModeChange,
  travelModeDisabled = false,
  editingPinForLeadId = null,
  onPinDragEnd,
  liveTracking = false,
  onPositionUpdate,
  radiusMiles = 5,
  showRadius = false,
  allowPinDrop = false,
  onPinDrop,
  pinDropLocation = null,
  heatmapData,
  heatmapType = "knocks",
  showHeatmap = false,
  className = "",
}: RouteMapProps) {
  // Filter out stops that don't have real coordinates BEFORE any downstream code
  // touches them. Mapbox's `Marker` throws "Invalid LngLat object: (NaN, NaN)"
  // if longitude/latitude are null/undefined/NaN/out-of-range, which crashes
  // the entire map and bubbles up to the error boundary. Common cause: leads
  // imported without lat/lng in their CSV (geocoding hasn't run yet, or the
  // address couldn't geocode). Also dedupe via the same valid-coord check
  // because the rest of the render path (fitBounds, fly-to, route polyline)
  // reads the same array.
  const stops = useMemo(() => {
    const valid = (stopsRaw || []).filter((s) => {
      const lng = Number(s?.longitude);
      const lat = Number(s?.latitude);
      return (
        Number.isFinite(lng) && Number.isFinite(lat) &&
        lng >= -180 && lng <= 180 &&
        lat >= -90 && lat <= 90
      );
    });

    // Cluster stops that share (or nearly share) coordinates and fan them
    // out around their centroid so each pin is visually distinct. Triggers
    // when multiple leads geocode to the same spot — usually duplicate or
    // multi-policy leads at the same household, or messy address data
    // missing the street name. Database coords aren't touched; we just
    // attach a `displayLatitude` / `displayLongitude` for rendering.
    const CLUSTER_RADIUS_METERS = 25;
    const FAN_RADIUS_METERS = 18; // how far to push duplicates out from center
    const distMeters = (a: any, b: any) => {
      const R = 6371000;
      const toRad = (d: number) => (d * Math.PI) / 180;
      const dLat = toRad(b.latitude - a.latitude);
      const dLng = toRad(b.longitude - a.longitude);
      const x = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
    };

    // Greedy single-link clustering — for small N (route stops typically ≤
    // 50) this is plenty fast.
    const clusters: any[][] = [];
    for (const stop of valid) {
      let placed = false;
      for (const cluster of clusters) {
        if (distMeters(cluster[0], stop) <= CLUSTER_RADIUS_METERS) {
          cluster.push(stop);
          placed = true;
          break;
        }
      }
      if (!placed) clusters.push([stop]);
    }

    // Spread each multi-stop cluster around its centroid. Single-stop
    // clusters keep their original coords.
    const result: any[] = [];
    for (const cluster of clusters) {
      if (cluster.length === 1) {
        result.push({ ...cluster[0], displayLatitude: cluster[0].latitude, displayLongitude: cluster[0].longitude });
        continue;
      }
      const centerLat = cluster.reduce((s, p) => s + p.latitude, 0) / cluster.length;
      const centerLng = cluster.reduce((s, p) => s + p.longitude, 0) / cluster.length;
      // Offset in degrees: 1° latitude ≈ 111000m. Longitude scales by cos(lat).
      const latOffsetPerMeter = 1 / 111000;
      const lngOffsetPerMeter = 1 / (111000 * Math.cos((centerLat * Math.PI) / 180));
      cluster.forEach((stop, idx) => {
        const angle = (2 * Math.PI * idx) / cluster.length;
        const dLat = Math.sin(angle) * FAN_RADIUS_METERS * latOffsetPerMeter;
        const dLng = Math.cos(angle) * FAN_RADIUS_METERS * lngOffsetPerMeter;
        result.push({
          ...stop,
          displayLatitude: centerLat + dLat,
          displayLongitude: centerLng + dLng,
        });
      });
    }

    // Preserve original order so indices align with parent's stopDetails.
    // Plain object instead of `new Map(...)` because `Map` here is the
    // imported react-map-gl component, not the JS Map constructor.
    const byLeadId: Record<string, any> = {};
    for (const s of result) byLeadId[s.leadId] = s;
    return valid.map((s) => byLeadId[s.leadId] || s);
  }, [stopsRaw]);

  const mapRef = useRef<MapRef>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [userPos, setUserPos] = useState<{ lat: number; lng: number } | null>(null);
  const [mapStyle, setMapStyle] = useState<"light" | "streets" | "satellite">("light");
  const [heatmapVisible, setHeatmapVisible] = useState(showHeatmap);
  // 3D building extrusions render from OSM building heights — free via
  // OpenFreeMap. Only meaningful on the vector styles (Clean / Streets);
  // Satellite is raster so the layer simply won't appear there.
  const [buildings3D, setBuildings3D] = useState(false);
  const watchIdRef = useRef<number | null>(null);
  // Latest onPositionUpdate without resubscribing the geolocation watch when
  // the parent re-renders (advanced-use-latest pattern).
  const onPositionUpdateRef = useRef(onPositionUpdate);
  onPositionUpdateRef.current = onPositionUpdate;

  const fitBounds = useCallback(() => {
    if (!mapRef.current || stops.length === 0) return;

    const lngs = stops.map((s) => s.longitude);
    const lats = stops.map((s) => s.latitude);

    if (userPos) {
      lngs.push(userPos.lng);
      lats.push(userPos.lat);
    }

    mapRef.current.fitBounds(
      [
        [Math.min(...lngs) - 0.02, Math.min(...lats) - 0.02],
        [Math.max(...lngs) + 0.02, Math.max(...lats) + 0.02],
      ],
      { padding: 60, duration: 800 },
    );
  }, [stops, userPos]);

  // Only fit full bounds on initial load, not on every update
  const initialFitDone = useRef(false);
  useEffect(() => {
    if (mapLoaded && !initialFitDone.current) {
      fitBounds();
      initialFitDone.current = true;
    }
  }, [mapLoaded, fitBounds]);

  // When active stop changes, zoom IN tight on the stop. We used to
  // fitBounds over active + next 4 stops, but if those next stops were
  // spread across the route the bounds ballooned and the map zoomed
  // out instead — opposite of what the agent wants when tapping a pin.
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    // Look up by leadId first (safe across the filter); fall back to index
    // for any caller still using the old activeStopIndex API.
    const stop = activeStopLeadId
      ? stops.find((s) => s.leadId === activeStopLeadId)
      : (activeStopIndex !== undefined ? stops[activeStopIndex] : null);
    if (!stop) return;
    const map = mapRef.current.getMap?.();
    const currentZoom = map?.getZoom?.() ?? 14;
    // Don't zoom OUT — only zoom in if currently more zoomed-out than 15.
    const targetZoom = currentZoom < 15 ? 15.5 : currentZoom;
    mapRef.current.flyTo({
      center: [stop.longitude, stop.latitude],
      zoom: targetZoom,
      duration: 700,
      essential: true,
    });
  }, [activeStopLeadId, activeStopIndex, mapLoaded, stops]);

  // Live GPS tracking.
  // ⚠️ On the NATIVE shell this must go through @capacitor/geolocation
  // (CoreLocation): WKWebView's navigator.geolocation prompted for permission
  // but then silently errored — the live dot never appeared even though the
  // user granted access. Browser keeps navigator.geolocation.
  useEffect(() => {
    if (!liveTracking) return;

    const handleFix = (pos: { coords: { latitude: number; longitude: number; accuracy: number; speed: number | null; heading: number | null } }) => {
      setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      // Share the fix with the parent (live-ping loop) so it doesn't have to
      // run its own GPS consumer. Ref keeps this effect's deps stable.
      onPositionUpdateRef.current?.({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        speed: pos.coords.speed,
        heading: pos.coords.heading,
      });
    };

    let cancelled = false;
    let capWatchId: string | null = null;

    (async () => {
      try {
        const { Capacitor } = await import("@capacitor/core");
        if (Capacitor.isNativePlatform()) {
          const { Geolocation } = await import("@capacitor/geolocation");
          try {
            const perm = await Geolocation.requestPermissions();
            if (perm.location === "denied") return;
          } catch {}
          try {
            const first = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
            if (!cancelled) handleFix(first as any);
          } catch {}
          capWatchId = await Geolocation.watchPosition(
            { enableHighAccuracy: true, timeout: 15000 },
            (position) => {
              if (!cancelled && position) handleFix(position as any);
            },
          );
          return;
        }
      } catch {}

      // Browser path
      if (!navigator.geolocation || cancelled) return;
      navigator.geolocation.getCurrentPosition(handleFix as any, () => {}, {
        enableHighAccuracy: true,
      });
      watchIdRef.current = navigator.geolocation.watchPosition(
        handleFix as any,
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
      );
    })();

    return () => {
      cancelled = true;
      if (watchIdRef.current !== null) {
        navigator.geolocation?.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (capWatchId) {
        import("@capacitor/geolocation")
          .then(({ Geolocation }) => Geolocation.clearWatch({ id: capWatchId! }))
          .catch(() => {});
      }
    };
  }, [liveTracking]);

  // Live ETA to next active stop — refreshes from OSRM /route as the agent
  // moves so the "minutes to next stop" displayed on the map reflects real
  // current position, not the original optimization estimate. Throttled to
  // at most once every 30s and only when the user has moved ≥100m to avoid
  // hammering the public OSRM demo while parked at a doorstep.
  const [liveEta, setLiveEta] = useState<{ seconds: number; meters: number } | null>(null);

  // Format helpers — keep raw seconds/meters in state so the badge can
  // re-render the right unit (min vs hr, ft vs mi) without re-fetching
  // OSRM. "749 min" was unreadable; "12h 29m" is what an agent expects.
  const formatEtaTime = (seconds: number): string => {
    const mins = Math.max(1, Math.round(seconds / 60));
    if (mins < 60) return `${mins} min`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
  };
  const formatEtaDistance = (meters: number): string => {
    const miles = meters / 1609.34;
    if (miles < 0.1) return `${Math.round(meters * 3.281)} ft`;
    if (miles < 10) return `${miles.toFixed(1)} mi`;
    return `${Math.round(miles)} mi`;
  };
  const lastEtaRef = useRef<{ at: number; lat: number; lng: number; targetId: string } | null>(null);

  // The "Next stop" the agent is actually driving to = first stop in the
  // sequence that's not visited and not skipped. NOT the same as
  // activeStopIndex (which can be any stop the agent tapped to inspect).
  const nextDriveTarget = useMemo(
    () => stops.find((s: any) => !s?.visited && !s?.skipped) || null,
    [stops],
  );

  useEffect(() => {
    if (!liveTracking || !userPos || !nextDriveTarget) {
      setLiveEta(null);
      return;
    }

    const now = Date.now();
    const last = lastEtaRef.current;
    const targetChanged = !last || last.targetId !== nextDriveTarget.leadId;
    const movedFar = !last
      || ((Math.abs(last.lat - userPos.lat) * 111000) > 100
          || (Math.abs(last.lng - userPos.lng) * 111000) > 100);
    const stale = !last || (now - last.at) > 30000;
    if (!targetChanged && !movedFar && !stale) return;

    lastEtaRef.current = { at: now, lat: userPos.lat, lng: userPos.lng, targetId: nextDriveTarget.leadId };
    const url = `https://router.project-osrm.org/route/v1/driving/${userPos.lng},${userPos.lat};${nextDriveTarget.longitude},${nextDriveTarget.latitude}?overview=false`;
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 8000);
    fetch(url, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const route = data?.routes?.[0];
        if (!route) return;
        setLiveEta({
          seconds: route.duration || 0,
          meters: route.distance || 0,
        });
      })
      .catch(() => {})
      .finally(() => clearTimeout(timeout));
  }, [userPos, nextDriveTarget, liveTracking]);

  const geojsonRoute = routeGeometry
    ? (() => {
        try {
          return JSON.parse(routeGeometry);
        } catch {
          return null;
        }
      })()
    : null;

  // Highlight ONLY the leg from last-completed → next unvisited stop. We fetch
  // this as its OWN OSRM polyline so the highlight is always exact, never a
  // mis-sliced subsection of the full route polyline (the full polyline
  // winds back near earlier stops, which made geometry-snap unreliable).
  const [activeLegGeoJson, setActiveLegGeoJson] = useState<any>(null);

  // Highlight the leg the agent is currently driving — from the most
  // recently completed stop to the next-up stop. When stop 3 is checked
  // off, the orange highlights 3→4 (the leg they're about to drive). When
  // 4 gets checked, it advances to 4→5. Cases:
  //   1. Route in progress, no stops done yet → highlight live GPS → stop[0].
  //   2. ≥1 stop done → highlight last-done → first-undone.
  // Otherwise (Build Route preview, planned route with no GPS) → no highlight.
  const activeLegPair = useMemo(() => {
    if (stops.length < 1) return null;
    const isDone = (s: any) => !!(s?.visited || s?.skipped);

    // Find last-done index and first-undone index by walking the array.
    let lastDoneIdx = -1;
    for (let i = 0; i < stops.length; i++) {
      if (isDone(stops[i])) lastDoneIdx = i;
    }
    let nextUpIdx = -1;
    for (let i = 0; i < stops.length; i++) {
      if (!isDone(stops[i])) { nextUpIdx = i; break; }
    }
    if (nextUpIdx < 0) return null; // route fully done

    // Case 1 — pre-first-knock: anchor from live GPS when available.
    if (lastDoneIdx < 0) {
      if (!liveTracking || !userPos) return null;
      return {
        fromStop: { longitude: userPos.lng, latitude: userPos.lat },
        toStop: stops[nextUpIdx],
      };
    }

    // Case 2 — between stops: last-done → next-up.
    return { fromStop: stops[lastDoneIdx], toStop: stops[nextUpIdx] };
  }, [stops, liveTracking, userPos]);

  useEffect(() => {
    if (!activeLegPair) { setActiveLegGeoJson(null); return; }
    const { fromStop, toStop } = activeLegPair;
    // Without a "from" stop (route hasn't started yet) we still want the leg
    // into stop[0] — but with no anchor we just leave the highlight off.
    if (!fromStop) { setActiveLegGeoJson(null); return; }

    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    const url =
      `https://router.project-osrm.org/route/v1/driving/` +
      `${fromStop.longitude},${fromStop.latitude};${toStop.longitude},${toStop.latitude}` +
      `?geometries=geojson&overview=full&steps=false`;

    fetch(url, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.routes?.[0]?.geometry) return;
        setActiveLegGeoJson({
          type: "Feature",
          geometry: data.routes[0].geometry,
          properties: {},
        });
      })
      .catch(() => {})
      .finally(() => clearTimeout(timeout));

    return () => { cancelled = true; controller.abort(); };
  }, [activeLegPair]);

  // OpenFreeMap + MapLibre needs no API key, so this branch should never
  // fire in normal operation. Kept as a defensive guard in case the tile
  // CDN is unreachable from the user's network.
  if (false) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 rounded-xl border border-gray-200 ${className}`}
      >
        <p className="text-sm text-gray-500">
          Map unavailable — please check your network connection
        </p>
      </div>
    );
  }

  return (
    // data-no-ptr: the shell's pull-to-refresh must NEVER hijack touches on
    // the map — it was eating pinch-zoom and the +/- taps on iOS.
    <div data-no-ptr className={`rounded-xl overflow-hidden border border-gray-200 relative ${className}`} style={{ touchAction: "none" }}>
      {/* Drive/Walk toggle — anchored top-right just under MapLibre's
          built-in zoom controls (+/−). Only renders when the parent wired
          travelMode + onTravelModeChange. */}
      {/* Shell zoom controls — MapLibre's NavigationControl taps don't
          register reliably in the WKWebView; plain React buttons wired to
          the map instance always do. MapLibre's own control stack is hidden
          on the shell via globals.css. */}
      {typeof window !== "undefined" && window.location.pathname.startsWith("/m") && (
        // top-[56px]: BELOW the style-switcher chip row — at top-[10px] the
        // "+" collided with the 3D chip in the builder's longer chip row
        <div className="absolute top-[56px] right-[10px] z-10 flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md">
          <button
            type="button"
            aria-label="Zoom in"
            onClick={() => (mapRef.current as any)?.getMap?.()?.zoomIn()}
            className="flex h-10 w-10 items-center justify-center text-[20px] font-semibold text-gray-700 active:bg-gray-100"
          >
            +
          </button>
          <button
            type="button"
            aria-label="Zoom out"
            onClick={() => (mapRef.current as any)?.getMap?.()?.zoomOut()}
            className="flex h-10 w-10 items-center justify-center border-t border-gray-200 text-[20px] font-semibold text-gray-700 active:bg-gray-100"
          >
            −
          </button>
        </div>
      )}

      {travelMode && onTravelModeChange && (
        <div className="absolute top-[100px] right-[10px] z-10 flex flex-col bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => travelMode !== "driving" && onTravelModeChange("driving")}
            disabled={travelModeDisabled}
            className={`flex items-center justify-center w-[29px] h-[29px] transition-colors ${
              travelMode === "driving" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Optimize for driving"
            aria-label="Drive mode"
          >
            <Car className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => travelMode !== "walking" && onTravelModeChange("walking")}
            disabled={travelModeDisabled}
            className={`flex items-center justify-center w-[29px] h-[29px] border-t border-gray-200 transition-colors ${
              travelMode === "walking" ? "bg-primary text-white" : "bg-white text-gray-600 hover:bg-gray-50"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Optimize for walking (dense neighborhoods, apartments)"
            aria-label="Walk mode"
          >
            <Footprints className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Top-left controls */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
        {/* Live tracking indicator */}
        {liveTracking && userPos && (
          <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-sm border border-gray-200">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
            </span>
            <span className="text-[10px] font-medium text-gray-600">Live</span>
          </div>
        )}

        {/* Live ETA to next stop — recalculated from current GPS via OSRM
            so the displayed minutes match what the agent actually has left
            from where they're standing right now (not the original optimize
            estimate from the route start). */}
        {liveTracking && liveEta && (
          <div className="flex items-center gap-1.5 bg-primary/95 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-sm">
            <span className="text-[10px] font-medium text-white/80">Next:</span>
            <span className="text-[11px] font-bold text-white tabular-nums">
              {formatEtaTime(liveEta.seconds)} · {formatEtaDistance(liveEta.meters)}
            </span>
          </div>
        )}

        {/* Map style toggle */}
        <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {(["light", "streets", "satellite"] as const).map((style) => {
            const label = style === "light" ? "Clean" : style === "streets" ? "Streets" : "Satellite";
            const tooltip =
              style === "light" ? "Clean map view — minimal labels"
              : style === "streets" ? "Street map view — full road labels"
              : "Satellite imagery — see actual rooftops";
            return (
              <button
                key={style}
                onClick={() => setMapStyle(style)}
                className={`px-2.5 py-1.5 text-[10px] font-medium transition-colors ${
                  mapStyle === style
                    ? "bg-primary text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                title={tooltip}
                aria-label={`Switch to ${label} map style`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Fit-all button — zoom out to show every stop in the route */}
        {stops.length > 1 && (
          <button
            onClick={() => fitBounds()}
            className="px-2.5 py-1.5 text-[10px] font-medium rounded-lg shadow-sm border bg-white/90 backdrop-blur-sm text-gray-600 border-gray-200 hover:bg-gray-100"
            title="Fit all stops in view"
          >
            Fit All
          </button>
        )}

        {/* Pin drop toggle */}
        {allowPinDrop && (
          <button
            onClick={() => {
              if (pinDropLocation) {
                onPinDrop?.(0, 0); // Signal to clear pin
              }
            }}
            className={`px-2.5 py-1.5 text-[10px] font-medium rounded-lg shadow-sm border transition-colors ${
              pinDropLocation
                ? "bg-primary text-white border-primary"
                : "bg-white/90 backdrop-blur-sm text-gray-600 border-gray-200"
            }`}
            title={pinDropLocation ? "Remove the dropped pin" : "Tap anywhere on the map to drop a pin and find leads near it"}
            aria-label={pinDropLocation ? "Clear dropped pin" : "Drop a pin on the map"}
          >
            {pinDropLocation ? "Clear Pin" : "Tap map to drop pin"}
          </button>
        )}

        {/* Heatmap toggle */}
        {heatmapData && heatmapData.length > 0 && (
          <button
            onClick={() => setHeatmapVisible(!heatmapVisible)}
            className={`px-2.5 py-1.5 text-[10px] font-medium rounded-lg shadow-sm border transition-colors ${
              heatmapVisible
                ? "bg-orange-500 text-white border-orange-500"
                : "bg-white/90 backdrop-blur-sm text-gray-600 border-gray-200 hover:bg-gray-100"
            }`}
            title={heatmapVisible ? "Hide the activity heatmap" : "Show heatmap of past door-knocks & conversations"}
            aria-label={heatmapVisible ? "Hide heatmap" : "Show heatmap"}
          >
            {heatmapVisible ? "Heatmap On" : "Heatmap"}
          </button>
        )}

        {/* 3D buildings toggle — vector styles only. Hidden on Satellite
            since Esri imagery is raster and has no building geometry. */}
        {mapStyle !== "satellite" && (
          <button
            onClick={() => setBuildings3D(!buildings3D)}
            className={`px-2.5 py-1.5 text-[10px] font-medium rounded-lg shadow-sm border transition-colors ${
              buildings3D
                ? "bg-primary text-white border-primary"
                : "bg-white/90 backdrop-blur-sm text-gray-600 border-gray-200 hover:bg-gray-100"
            }`}
            title={buildings3D ? "Turn off 3D buildings" : "Show 3D buildings — tilt and zoom in to see them"}
            aria-label={buildings3D ? "Turn off 3D buildings" : "Turn on 3D buildings"}
          >
            {buildings3D ? "3D On" : "3D"}
          </button>
        )}
      </div>

      <Map
        ref={mapRef}
        initialViewState={{
          longitude: stops[0]?.longitude || -98.5,
          latitude: stops[0]?.latitude || 39.8,
          zoom: 10,
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle={MAP_STYLES[mapStyle]}
        workerUrl={MAPLIBRE_WORKER_URL}
        onLoad={() => setMapLoaded(true)}
        onClick={(e: any) => {
          if (allowPinDrop && e.lngLat) {
            onPinDrop?.(e.lngLat.lat, e.lngLat.lng);
            return;
          }
          // Map background tap (markers stopPropagation in their own onClick,
          // so this only fires when the click missed any stop pin).
          onMapBackgroundClick?.();
        }}
        cursor={allowPinDrop ? "crosshair" : undefined}
        attributionControl={false}
      >
        <NavigationControl position="top-right" showCompass={true} />

        {/* 3D buildings — fill-extrusion driven by OSM building heights from
            OpenFreeMap's openmaptiles vector source. minzoom 14 keeps it out
            of the way at city/state zoom levels. Switch off automatically on
            Satellite (raster, no vector building data). */}
        {buildings3D && mapStyle !== "satellite" && (
          <Layer
            id="route-3d-buildings"
            type="fill-extrusion"
            source="openmaptiles"
            source-layer="building"
            minzoom={14}
            paint={{
              "fill-extrusion-color": [
                "interpolate",
                ["linear"],
                ["coalesce", ["get", "render_height"], 5],
                0, "#e5e7eb",
                25, "#cbd5e1",
                75, "#94a3b8",
                150, "#64748b",
              ],
              "fill-extrusion-height": [
                "interpolate", ["linear"], ["zoom"],
                14, 0,
                15, ["coalesce", ["get", "render_height"], 5],
              ],
              "fill-extrusion-base": [
                "interpolate", ["linear"], ["zoom"],
                14, 0,
                15, ["coalesce", ["get", "render_min_height"], 0],
              ],
              "fill-extrusion-opacity": 0.85,
            }}
            // Skip features without render_height — when MapLibre tries to
            // evaluate the height expression on a building with null height,
            // it spams "Expected value to be of type number, but found null"
            // every tile parse. Only render buildings that have height data.
            filter={["all", ["!=", ["get", "hide_3d"], true], ["has", "render_height"]]}
          />
        )}

        {/* Route line */}
        {geojsonRoute && (
          <Source id="route" type="geojson" data={geojsonRoute}>
            <Layer
              id="route-line-shadow"
              type="line"
              paint={{
                "line-color": "#000",
                "line-width": 6,
                "line-opacity": 0.08,
              }}
            />
            <Layer
              id="route-line"
              type="line"
              paint={{
                "line-color": mapStyle === "satellite" ? "#00e5ff" : "#4338ca",
                "line-width": mapStyle === "satellite" ? 4 : 3.5,
                "line-opacity": 0.55,
              }}
            />
          </Source>
        )}

        {/* Active-leg highlight — the segment leading into the next stop. */}
        {activeLegGeoJson && (
          <Source id="route-active-leg" type="geojson" data={activeLegGeoJson as any}>
            <Layer
              id="route-active-leg-glow"
              type="line"
              paint={{
                "line-color": "#f97316",
                "line-width": 10,
                "line-opacity": 0.25,
                "line-blur": 4,
              }}
            />
            <Layer
              id="route-active-leg-line"
              type="line"
              paint={{
                "line-color": "#f97316",
                "line-width": 5,
                "line-opacity": 1,
              }}
              layout={{ "line-cap": "round", "line-join": "round" }}
            />
          </Source>
        )}

        {/* Heatmap layer */}
        {heatmapVisible && heatmapData && heatmapData.length > 0 && (
          <Source
            id="heatmap-data"
            type="geojson"
            data={{
              type: "FeatureCollection",
              features: heatmapData.map((p) => ({
                type: "Feature" as const,
                geometry: { type: "Point" as const, coordinates: [p.lng, p.lat] },
                properties: { weight: p.weight || 1 },
              })),
            }}
          >
            <Layer
              id="heatmap-layer"
              type="heatmap"
              paint={{
                "heatmap-weight": ["get", "weight"],
                "heatmap-intensity": 1,
                "heatmap-radius": 30,
                "heatmap-opacity": 0.6,
                "heatmap-color": heatmapType === "conversions"
                  ? [
                      "interpolate", ["linear"], ["heatmap-density"],
                      0, "rgba(0,0,0,0)",
                      0.2, "rgba(16,185,129,0.3)",
                      0.4, "rgba(16,185,129,0.5)",
                      0.6, "rgba(5,150,105,0.7)",
                      0.8, "rgba(4,120,87,0.85)",
                      1, "rgba(6,78,59,1)",
                    ]
                  : heatmapType === "cold"
                    ? [
                        "interpolate", ["linear"], ["heatmap-density"],
                        0, "rgba(0,0,0,0)",
                        0.2, "rgba(59,130,246,0.3)",
                        0.4, "rgba(59,130,246,0.5)",
                        0.6, "rgba(37,99,235,0.7)",
                        0.8, "rgba(29,78,216,0.85)",
                        1, "rgba(30,64,175,1)",
                      ]
                    : [
                        "interpolate", ["linear"], ["heatmap-density"],
                        0, "rgba(0,0,0,0)",
                        0.2, "rgba(251,191,36,0.3)",
                        0.4, "rgba(245,158,11,0.5)",
                        0.6, "rgba(234,88,12,0.7)",
                        0.8, "rgba(220,38,38,0.85)",
                        1, "rgba(185,28,28,1)",
                      ],
              }}
            />
          </Source>
        )}

        {/* Pin drop marker + radius. NaN guard: a truthy `pinDropLocation`
            object can still carry `{ lat: NaN, lng: NaN }` (e.g. geocode
            failure upstream), and `??` won't catch NaN — only nullish.
            MapLibre throws "Invalid LngLat object: (NaN, NaN)" on mount. */}
        {pinDropLocation && Number.isFinite(pinDropLocation.lng) && Number.isFinite(pinDropLocation.lat) && (
          <>
            <Marker
              longitude={pinDropLocation.lng}
              latitude={pinDropLocation.lat}
              anchor="center"
            >
              <div className="w-5 h-5 rounded-full bg-primary border-[3px] border-white shadow-lg" />
            </Marker>
            {radiusMiles > 0 && (
              <Source
                id="pin-radius"
                type="geojson"
                data={createGeoJSONCircle(pinDropLocation.lng, pinDropLocation.lat, radiusMiles)}
              >
                <Layer
                  id="pin-radius-fill"
                  type="fill"
                  paint={{ "fill-color": "#4338ca", "fill-opacity": 0.06 }}
                />
                <Layer
                  id="pin-radius-border"
                  type="line"
                  paint={{ "line-color": "#4338ca", "line-width": 2, "line-opacity": 0.3, "line-dasharray": [4, 3] }}
                />
              </Source>
            )}
          </>
        )}

        {/* Radius circle around user position */}
        {showRadius && userPos && Number.isFinite(userPos.lng) && Number.isFinite(userPos.lat) && radiusMiles > 0 && !pinDropLocation && (
          <Source
            id="radius-circle"
            type="geojson"
            data={createGeoJSONCircle(userPos.lng, userPos.lat, radiusMiles)}
          >
            <Layer
              id="radius-fill"
              type="fill"
              paint={{
                "fill-color": "#4338ca",
                "fill-opacity": 0.06,
              }}
            />
            <Layer
              id="radius-border"
              type="line"
              paint={{
                "line-color": "#4338ca",
                "line-width": 2,
                "line-opacity": 0.3,
                "line-dasharray": [4, 3],
              }}
            />
          </Source>
        )}

        {/* Start marker — shows at GPS or first stop during in_progress.
            Guard against rendering when neither userPos nor stops[0] exists
            so we never pass undefined to <Marker>. `??` only catches null /
            undefined, NOT NaN — so we resolve coords explicitly and run
            isFinite on the result before mounting the marker. MapLibre
            throws "Invalid LngLat object: (NaN, NaN)" otherwise. */}
        {(() => {
          if (!liveTracking) return null;
          const lng = Number.isFinite(userPos?.lng as number)
            ? (userPos!.lng)
            : Number(stops[0]?.longitude);
          const lat = Number.isFinite(userPos?.lat as number)
            ? (userPos!.lat)
            : Number(stops[0]?.latitude);
          if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
          return (
            <Marker
              longitude={lng}
              latitude={lat}
              anchor="bottom"
              offset={[0, -30]}
            >
              <div className="flex items-center gap-1 bg-primary text-white px-2.5 py-1.5 rounded-lg shadow-lg text-[11px] font-bold whitespace-nowrap">
                🚗 START
              </div>
            </Marker>
          );
        })()}

        {/* Finish flag at last stop — only during in_progress.
            `stops` is filtered for valid coords above, but defend in depth. */}
        {liveTracking && stops.length > 1
          && Number.isFinite(stops[stops.length - 1].longitude)
          && Number.isFinite(stops[stops.length - 1].latitude) && (
          <Marker
            longitude={stops[stops.length - 1].longitude}
            latitude={stops[stops.length - 1].latitude}
            anchor="bottom"
            offset={[0, -20]}
          >
            <div className="flex items-center gap-1 bg-gray-900 text-white px-2 py-1 rounded-lg shadow-lg text-[10px] font-bold whitespace-nowrap">
              🏁 END
            </div>
          </Marker>
        )}

        {/* Live user position — blue pulsing dot (when route is started) */}
        {userPos && liveTracking && Number.isFinite(userPos.lng) && Number.isFinite(userPos.lat) && (
          <Marker
            longitude={userPos.lng}
            latitude={userPos.lat}
            anchor="center"
          >
            <div className="relative">
              {/* Pulse ring */}
              <div className="absolute -inset-3 rounded-full bg-blue-500/20 animate-ping" style={{ animationDuration: "2s" }} />
              {/* Outer ring */}
              <div className="absolute -inset-2 rounded-full bg-blue-500/15" />
              {/* Blue dot */}
              <div className="relative w-4 h-4 rounded-full bg-blue-500 border-[2.5px] border-white shadow-lg" />
            </div>
          </Marker>
        )}

        {/* Stop markers — sort so the active marker is rendered LAST,
            putting it visually on top of any overlapping pins (poor
            geocoding sometimes places multiple stops at nearly identical
            coords, causing the active highlight to be hidden underneath
            a non-active marker rendered later in the natural iteration). */}
        {[...stops]
          .map((stop, originalIdx) => ({ stop, originalIdx }))
          .sort((a, b) => {
            const aActive = activeStopLeadId
              ? a.stop.leadId === activeStopLeadId
              : activeStopIndex === a.originalIdx;
            const bActive = activeStopLeadId
              ? b.stop.leadId === activeStopLeadId
              : activeStopIndex === b.originalIdx;
            if (aActive === bActive) return 0;
            return aActive ? 1 : -1; // active goes last → renders on top
          })
          .map(({ stop, originalIdx: i }) => {
          // Match by leadId when provided, fall back to index. The leadId
          // path is correct even if the parent's index points into an
          // unfiltered list while this `stops` is the filtered subset.
          const isActive = activeStopLeadId
            ? stop.leadId === activeStopLeadId
            : activeStopIndex === i;
          const isVisited = stop.visited;
          const isSkipped = stop.skipped;
          const isNotHome = stop.notHome;
          const isEditing = editingPinForLeadId === stop.leadId;

          // Resolve coords with NaN guard. `??` only catches null/undefined,
          // so a NaN displayLongitude (e.g. cluster math went sideways with
          // a single-element cluster at the poles, etc.) would slip past.
          const lngVal = Number.isFinite((stop as any).displayLongitude)
            ? (stop as any).displayLongitude
            : Number(stop.longitude);
          const latVal = Number.isFinite((stop as any).displayLatitude)
            ? (stop as any).displayLatitude
            : Number(stop.latitude);
          if (!Number.isFinite(lngVal) || !Number.isFinite(latVal)) return null;

          return (
            <Marker
              key={stop.leadId}
              // Use display coords (fan-out for overlapping stops) so
              // duplicates at the same address don't stack invisibly. Real
              // lat/lng stays in stop.latitude / stop.longitude for
              // navigation links + drag-to-fix saves.
              longitude={lngVal}
              latitude={latVal}
              anchor="center"
              draggable={isEditing}
              onDragEnd={(e: any) => {
                if (!isEditing || !onPinDragEnd) return;
                const lng = e?.lngLat?.lng;
                const lat = e?.lngLat?.lat;
                if (Number.isFinite(lat) && Number.isFinite(lng)) {
                  onPinDragEnd(stop.leadId, lat, lng);
                }
              }}
              onClick={(e: any) => {
                e.originalEvent?.stopPropagation();
                if (isEditing) return; // suppress stop-click while dragging-mode
                onStopClick?.(stop.leadId);
              }}
            >
              <div className="flex flex-col items-center">
                <div
                  className={`
                    relative flex items-center justify-center rounded-full
                    text-[11px] font-bold transition-all duration-200 select-none
                    ${isEditing ? "cursor-move w-10 h-10 bg-amber-500 text-white ring-4 ring-amber-300 ring-offset-2 shadow-lg animate-pulse z-20" : "cursor-pointer"}
                    ${
                      isEditing
                        ? ""
                        : isActive
                          ? "w-9 h-9 bg-primary text-white ring-4 ring-primary/20 shadow-lg scale-110 z-10"
                          : isVisited
                            ? "w-7 h-7 bg-emerald-500 text-white shadow-sm"
                            : isNotHome
                              ? "w-7 h-7 bg-amber-400 text-white shadow-sm"
                              : isSkipped
                                ? "w-7 h-7 bg-gray-300 text-gray-600 shadow-sm"
                                : stop.order > 0
                                  ? "w-7 h-7 bg-primary text-white shadow-sm"
                                  : "w-4 h-4 bg-primary/40 shadow-sm"
                    }
                  `}
                >
                  {/* In edit mode, swap the stop number for a pencil so the
                      agent has a clear visual cue that THIS pin is the one
                      they're correcting (vs. all other pins on the route). */}
                  {isEditing
                    ? <Pencil className="w-5 h-5" strokeWidth={2.5} />
                    : (isVisited ? "\u2713" : isNotHome ? "!" : stop.order > 0 ? stop.order : "")}
                </div>
                {isEditing && (
                  <span className="mt-1 text-[9px] font-bold text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded whitespace-nowrap shadow-sm">
                    EDITING — drag me
                  </span>
                )}
                {isNotHome && !isActive && (
                  <span className="mt-0.5 text-[8px] font-bold text-amber-600 bg-amber-50 px-1 rounded whitespace-nowrap">
                    NOT HOME
                  </span>
                )}
              </div>
            </Marker>
          );
        })}
      </Map>

      {/* InsuraCentral brand badge — bottom left */}
      <div className="absolute bottom-3 left-3 z-10 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-sm border border-gray-200">
        <img src="https://insuracentral.com/images/logo.svg" alt="InsuraCentral" className="h-5" />
      </div>
    </div>
  );
}
